import { FileTextOutlined, CheckCircleFilled, CloseCircleFilled, LoadingOutlined, CloseOutlined, ClockCircleOutlined } from "@ant-design/icons"
import { Dropdown } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { useEffect, useRef, useState } from "react"
import { importCotasSelector, clearImportCotas } from "@redux/slices/import-cotas"
import * as S from '../styled'

const RING_R = 20;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R;

const statusLabel: Record<string, string> = {
    AGUARDANDO: 'Aguardando',
    PROCESSANDO: 'Processando',
    CONCLUIDO: 'Concluído',
    ERRO: 'Erro',
};

export const ImportCotasIndicator = () => {
    const dispatch = useDispatch();
    const { jobInfo, isError, isImporting, fileName } = useSelector(importCotasSelector);

    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        if (!jobInfo?.iniciadoEm || !isImporting) {
            setElapsedSeconds(0);
            return;
        }

        const startTime = new Date(jobInfo.iniciadoEm).getTime();
        setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));

        timerRef.current = setInterval(() => {
            setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
        }, 1000);

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [jobInfo?.iniciadoEm, isImporting]);

    let uiStatus: 'loading' | 'success' | 'error' | 'idle' = 'idle';

    if (isImporting || jobInfo?.status === 'AGUARDANDO' || jobInfo?.status === 'PROCESSANDO') {
        uiStatus = 'loading';
    } else if (jobInfo?.status === 'CONCLUIDO') {
        uiStatus = 'success';
    } else if (jobInfo?.status === 'ERRO' || isError) {
        uiStatus = 'error';
    }

    if ((uiStatus as string) === 'idle' && !jobInfo) return null;

    const getTitle = () => {
        if (uiStatus === 'loading') return 'Importando...';
        if (uiStatus === 'success') return 'Importação finalizada com sucesso';
        if (uiStatus === 'error') return 'Erro na importação';
        return 'Importação de Cotas';
    };

    const ringColor =
        uiStatus === 'loading' ? '#1890ff' :
            uiStatus === 'error' ? '#ff4d4f' :
                '#52c41a';

    const progress = uiStatus === 'loading' ? 75 : 100;
    const dashOffset = RING_CIRCUMFERENCE - (progress / 100) * RING_CIRCUMFERENCE;

    const overlay = (
        <div style={{
            background: 'white',
            borderRadius: 8,
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            minWidth: 280,
            maxWidth: 340,
            overflow: 'hidden',
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                borderBottom: '1px solid #f0f0f0',
            }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: '#333' }}>
                    Importação de Cotas
                </span>
                {!isImporting && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            dispatch(clearImportCotas() as any);
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#999',
                            fontSize: 12,
                            padding: '0 2px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                        }}
                    >
                        <CloseOutlined style={{ fontSize: 10 }} />
                        Limpar
                    </button>
                )}
            </div>

            <div style={{ padding: '12px 14px' }}>
                {jobInfo ? (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            {uiStatus === 'loading' && (
                                <LoadingOutlined style={{ color: '#1890ff', fontSize: 14 }} spin />
                            )}
                            {uiStatus === 'success' && (
                                <CheckCircleFilled style={{ color: '#52c41a', fontSize: 14 }} />
                            )}
                            {uiStatus === 'error' && (
                                <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 14 }} />
                            )}
                            <span style={{ fontSize: 13, color: '#333' }}>
                                {statusLabel[jobInfo.status] ?? jobInfo.status}
                            </span>
                        </div>

                        {uiStatus === 'loading' && (
                            <div style={{ height: 4, background: '#f0f0f0', borderRadius: 2, overflow: 'hidden', marginBottom: 6 }}>
                                <div style={{
                                    height: '100%',
                                    background: '#1890ff',
                                    borderRadius: 2,
                                    width: '100%',
                                    animation: 'indeterminate 1.5s infinite ease-in-out',
                                }} />
                            </div>
                        )}

                        {isImporting && jobInfo?.iniciadoEm && (
                            <p style={{ margin: '0 0 4px', fontSize: 12, color: '#888', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <ClockCircleOutlined style={{ fontSize: 11 }} />
                                {String(Math.floor(elapsedSeconds / 60)).padStart(2, '0')}:{String(elapsedSeconds % 60).padStart(2, '0')}
                            </p>
                        )}

                        {fileName && (
                            <p style={{ margin: '0 0 4px', fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={fileName}>
                                Arquivo: <strong style={{ color: '#333' }}>{fileName}</strong>
                            </p>
                        )}

                        <p style={{ margin: 0, fontSize: 12, color: '#888' }}>
                            Linhas processadas: <strong style={{ color: '#333' }}>{jobInfo.linhasProcessadas}</strong>
                        </p>

                        {jobInfo.erros?.length > 0 && (
                            <div style={{ marginTop: 10, maxHeight: 140, overflowY: 'auto' }}>
                                <p style={{ margin: '0 0 4px', color: '#ff4d4f', fontSize: 12, fontWeight: 600 }}>
                                    Erros ({jobInfo.erros.length}):
                                </p>
                                {jobInfo.erros.slice(0, 10).map((err, idx) => (
                                    <p key={idx} style={{ margin: '0 0 2px', color: '#ff4d4f', fontSize: 11 }}>
                                        • {err}
                                    </p>
                                ))}
                                {jobInfo.erros.length > 10 && (
                                    <p style={{ margin: 0, fontSize: 11, color: '#888' }}>
                                        ...e mais {jobInfo.erros.length - 10}
                                    </p>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <p style={{ margin: 0, fontSize: 13, color: '#888' }}>Nenhum import recente</p>
                )}
            </div>
        </div>
    );

    return (
        <Dropdown overlay={overlay} trigger={['click']} placement="bottomRight">
            <S.IndicatorWrapper $status={uiStatus} title={getTitle()}>
                <S.IndicatorRingSvg $spinning={uiStatus === 'loading'} viewBox="0 0 48 48">
                    <circle
                        cx="24" cy="24" r={RING_R}
                        fill="none"
                        stroke="rgba(0,0,0,0.08)"
                        strokeWidth="2"
                    />
                    <circle
                        cx="24" cy="24" r={RING_R}
                        fill="none"
                        stroke={ringColor}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeDasharray={RING_CIRCUMFERENCE}
                        strokeDashoffset={uiStatus === 'loading' ? RING_CIRCUMFERENCE * 0.25 : dashOffset}
                        style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                    />
                </S.IndicatorRingSvg>

                <FileTextOutlined style={{
                    fontSize: 18,
                    position: 'relative',
                    zIndex: 1,
                    color: uiStatus === 'error' ? '#ff4d4f' : uiStatus === 'success' ? '#52c41a' : '#1890ff',
                }} />
            </S.IndicatorWrapper>
        </Dropdown>
    );
};
